import React from "react";
import { useNavigate } from 'react-router-dom';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Blue Header */}
      <div className="bg-blue-600 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center mb-4">
            <button onClick={() => navigate('/')} className="flex items-center text-white hover:text-blue-200">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to home
            </button>
          </div>
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-blue-100 text-lg">How we collect, use, and protect your information</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <p className="text-gray-500 mb-6">Last updated: June 5, 2025</p>
          
          {/* Important Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <h3 className="text-blue-800 font-semibold mb-2">Important Notice</h3>
            <p className="text-blue-700 text-sm">
              This is a website that is dedicated to a ROBLOX group. This service is not affiliated with the UK Government, Parliament, 
              or official UK petitions system.
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Introduction</h2>
            <p className="text-gray-600 mb-4">
              This privacy policy explains how UK RBLX Petitions ("we", "our", or "us") collects, uses, and protects your 
              personal information when you use our website and services.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mb-3">ROBLOX Authentication Data</h3>
            <p className="text-gray-600 mb-4">When you connect your ROBLOX account, we collect:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li><strong>ROBLOX Username:</strong> Your public ROBLOX display name</li>
              <li><strong>ROBLOX User ID:</strong> Your unique ROBLOX identifier</li>
              <li><strong>Group Membership Data:</strong> Your rank in ROBLOX group <a href="https://www.roblox.com/communities/541807/UK-United-Kingdom" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">United Kingdom</a> (used for petition signing verification)</li>
              <li><strong>Authentication Tokens:</strong> Temporary tokens to verify your identity</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mb-3">Usage Data</h3>
            <p className="text-gray-600 mb-4">We may collect:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li><strong>Browser Information:</strong> Browser type, version, and device information</li>
              <li><strong>Usage Analytics:</strong> Pages visited, time spent on site, and user interactions</li>
              <li><strong>IP Address:</strong> For security and analytics purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mb-3">Petition Data</h3>
            <p className="text-gray-600 mb-4">When you interact with petitions:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Petition Signatures:</strong> Record of petitions you have signed (if eligible)</li>
              <li><strong>Search Queries:</strong> Terms you search for on the website</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">How We Use Your Information</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mb-3">Authentication & Authorization</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li><strong>Verify Identity:</strong> Confirm you are a legitimate ROBLOX user</li>
              <li><strong>Check Permissions:</strong> Verify your rank in the <a href="https://www.roblox.com/communities/541807/UK-United-Kingdom" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">United Kingdom</a> to determine petition signing eligibility</li>
              <li><strong>Session Management:</strong> Keep you logged in during your visit</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-700 mb-3">Website Functionality</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Petition Management:</strong> Track your petition signatures and interactions</li>
              <li><strong>User Experience:</strong> Personalize your experience based on your permissions</li>
              <li><strong>Security:</strong> Prevent fraud and unauthorized access</li>
            </ul>
          </section>

          {/* Data Storage & Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Data Storage & Security</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mb-3">Security Measures</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>HTTPS Encryption:</strong> All data transmission is encrypted</li>
              <li><strong>OAuth Security:</strong> We use ROBLOX's secure OAuth 2.0 protocol</li>
              <li><strong>Access Controls:</strong> Limited access to any stored data</li>
              <li><strong>Regular Updates:</strong> Security measures are regularly reviewed and updated</li>
            </ul>
          </section>

          {/* Your Rights & Choices */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Your Rights & Choices</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mb-3">Account Control</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Disconnect:</strong> You can disconnect your ROBLOX account at any time</li>
              <li><strong>Access:</strong> You can request information about what data we have collected</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Third-Party Services</h2>
            
            <h3 className="text-xl font-semibold text-gray-700 mb-3">ROBLOX Integration</h3>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>OAuth Provider:</strong> We use ROBLOX's OAuth service for authentication</li>
              <li><strong>ROBLOX Privacy:</strong> Please review ROBLOX's Privacy Policy</li>
            </ul>
          </section>

          {/* Cookies & Tracking */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Cookies & Tracking</h2>
            <p className="text-gray-600">
              We use essential cookies for authentication and session management. You can control cookies through your 
              browser settings, but this may affect website functionality.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Children's Privacy</h2>
            <p className="text-gray-600">
              This website is intended for users aged 13 and above, consistent with ROBLOX's terms of service. We do not 
              knowingly collect personal information from children under 13.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this privacy policy from time to time. We will notify users of significant changes by updating the 
              "Last updated" date at the top of this policy.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Contact Information</h2>
            <p className="text-gray-600">
              If you have questions about this Privacy Policy, please message pqs. on Discord.
            </p>
          </section>

          {/* Bottom Notice */}
          <div className="mt-8 p-4 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600">
              By using this website, you acknowledge that you have read and understood this privacy policy and agree to the collection 
              and use of your information as described.
            </p>
          </div>

          {/* Navigation Links */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <a href="/" className="flex items-center text-blue-600 hover:text-blue-800">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Return to Home
            </a>
            <a href="/terms-and-conditions" className="text-blue-600 hover:text-blue-800">
              View Terms and Conditions →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Terms and Conditions Component
